/**
 * `/forgot-password` — request a reset link (auth-and-authz.md §1.5). ALWAYS renders a
 * non-revealing confirmation regardless of whether the email exists (the backend
 * likewise always returns `{ ok: true }`; CC-7). This is also where a
 * `force_password_change` user (breached deferred-HIBP at login) lands to recover.
 *
 * An optional `redirect` (an invitation / allowlisted return path, carried from /login) is
 * passed back on every "Back to sign in" link so the round trip does not lose it (F-33).
 */
import { Link, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent, type JSX } from 'react';

import { AuthCard, FormError, SubmitButton, TextField } from '../components/ui.tsx';
import { api, getErrorMessage } from '../lib/api.ts';
import { carriedRedirect } from '../router/redirect.ts';

export function ForgotPasswordScreen(): JSX.Element {
  const { redirect } = useSearch({ strict: false }) as { redirect?: string };
  const carry = carriedRedirect(redirect);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.auth.forgotPassword({ email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthCard title="Check your email">
        <div style={{ display: 'grid', gap: 16 }}>
          <p role="status" style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
            If an account exists for that address, a password-reset link is on its way. The link
            expires in 30 minutes. If it doesn't arrive, check your spam folder or request another.
          </p>
          <Link to="/login" search={carry} style={{ fontSize: 13 }}>
            Back to sign in
          </Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Reset your password" subtitle="We'll email you a link to set a new password">
      <form
        onSubmit={handleSubmit}
        aria-label="Request password reset"
        style={{ display: 'grid', gap: 12 }}
      >
        <TextField
          label="Work email"
          name="email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="username"
        />
        <FormError message={error} />
        <SubmitButton disabled={busy || email === ''}>
          {busy ? 'Sending…' : 'Send reset link'}
        </SubmitButton>
        <Link to="/login" search={carry} style={{ fontSize: 13 }}>
          Back to sign in
        </Link>
      </form>
    </AuthCard>
  );
}
